/**
 * NDA verification of industry accounts (`app/services/admin/nda_admin_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { assertPojo } from '@brainfocus/web/api/response';

// ========== NDA Management API (SuperAdmin only, #529) ==========

/**
 * List industry-player accounts and their NDA verification state.
 */
export async function getNdaAccounts(): Promise<NdaAccount[]> {
    const response = await client.request('GET', '/admin/nda/pending');
    assertPojo<{ accounts: NdaAccount[] }>(response);
    return (response as { accounts: NdaAccount[] }).accounts;
}

/**
 * Verify a pending account by uploading the signed NDA document and
 * activating access.
 */
export async function verifyNda(userId: number, file: File): Promise<NdaAccount> {
    const formData = new FormData();
    formData.append('userId', String(userId));
    formData.append('file', file);
    const response = await client.request('POST', '/admin/nda/verify', formData, { raw: true });
    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Verification failed' }));
        throw new Error(err.error || 'Verification failed');
    }
    return response.json();
}

/**
 * URL to download the stored NDA document for a verified account.
 */
export function ndaDocumentUrl(userId: number): string {
    return `/admin/nda/document/${userId}`;
}

/**
 * An industry-player account and its NDA verification state (#529, SuperAdmin only).
 */
export interface NdaAccount {
    id: number;
    email: string;
    fullName: string;
    institution: string | null;
    ndaVerified: boolean;
    ndaVerifiedAt: string | null;
    ndaVerifiedBy: string | null;
    ndaDocumentFilename: string | null;
}
